import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { EASE, scrollToId } from "./motion";

const HERO_IMG =
    "https://static.prod-images.emergentagent.com/jobs/042f1a63-d502-4eb1-8de6-71e5b27c4c81/images/694cf49a34483285e30a48dd2040a62e3836ba3c626ca489b37a5d43a240d74c.jpeg";
const MAILTO =
    "mailto:hello@inskinau.com.au?subject=Consultation%20request%20%E2%80%94%20In%20Skin";
const D = 1.45; // base delay — after the intro curtain lifts

function MaskLine({ children, delay, className = "" }) {
    return (
        <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
            <motion.span
                className={`block ${className}`}
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.15, delay, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
    const txtY = useTransform(scrollYProgress, [0, 1], [0, -70]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="relative overflow-hidden bg-cream px-6 pt-28 md:h-screen md:px-12 md:pt-0"
        >
            <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-14 md:h-full md:grid-cols-12">
                {/* Type */}
                <motion.div style={{ y: txtY }} className="md:col-span-7 md:pb-10">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: D, duration: 0.8 }}
                        className="mb-7 flex items-center gap-4"
                    >
                        <span className="h-px w-12 bg-clay" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-earth md:text-[11px]">
                            Clinical &amp; gut-skin studio — Australia
                        </span>
                    </motion.div>

                    <h1 className="font-serif text-[clamp(3.1rem,9vw,8.25rem)] font-light leading-[0.98] text-ink">
                        <MaskLine delay={D + 0.1}>Clinical skin.</MaskLine>
                        <MaskLine delay={D + 0.28} className="italic text-earth">
                            Elevated <span className="not-italic text-ink">you.</span>
                        </MaskLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: D + 0.5, duration: 0.9, ease: EASE }}
                        className="mt-8 max-w-md text-base font-light leading-relaxed text-earth md:text-lg"
                    >
                        Bespoke cosmeceutical treatments aligned with gut health,
                        dietary wisdom and daily ritual — true skin
                        transformation, from the inside out.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: D + 0.65, duration: 0.9, ease: EASE }}
                        className="mt-10 flex flex-wrap items-center gap-7"
                    >
                        <a
                            data-testid="hero-book-online-button"
                            href={MAILTO}
                            className="rounded-full bg-ink px-9 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-earth hover:shadow-xl"
                        >
                            Book online
                        </a>
                        <button
                            data-testid="hero-explore-store-button"
                            onClick={() => scrollToId("store")}
                            className="group flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-ink"
                        >
                            <span className="flex h-11 w-11 items-center justify-center rounded-full border border-ink/20 transition-all duration-300 group-hover:bg-ink group-hover:text-cream">
                                <ArrowDown size={15} strokeWidth={1.5} />
                            </span>
                            Explore our store
                        </button>
                    </motion.div>
                </motion.div>

                {/* Portrait */}
                <div className="relative flex justify-center md:col-span-5 md:h-screen md:items-center md:justify-end">
                    <motion.div style={{ y: imgY }} className="relative w-[82%] max-w-[420px] md:w-full md:max-w-[440px]">
                        <motion.div
                            initial={{ clipPath: "inset(100% 0 0 0)" }}
                            animate={{ clipPath: "inset(0% 0 0 0)" }}
                            transition={{ duration: 1.3, delay: D + 0.35, ease: EASE }}
                            className="aspect-[3/4] overflow-hidden rounded-b-[28px] rounded-t-[999px]"
                        >
                            <motion.img
                                src={HERO_IMG}
                                alt="A clinical facial treatment at the In Skin studio"
                                className="h-full w-full object-cover"
                                initial={{ scale: 1.25 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 1.7, delay: D + 0.35, ease: EASE }}
                            />
                        </motion.div>

                        {/* Rotating seal */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.6 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: D + 1.05, duration: 0.8, ease: EASE }}
                            className="absolute -left-8 bottom-10 h-28 w-28 md:-left-16 md:h-32 md:w-32"
                        >
                            <div className="relative flex h-full w-full items-center justify-center rounded-full bg-ink shadow-2xl">
                                <svg
                                    viewBox="0 0 120 120"
                                    className="absolute inset-0 h-full w-full animate-[spin_18s_linear_infinite]"
                                >
                                    <defs>
                                        <path
                                            id="heroCircle"
                                            d="M60,60 m-44,0 a44,44 0 1,1 88,0 a44,44 0 1,1 -88,0"
                                        />
                                    </defs>
                                    <text
                                        className="fill-cream/90"
                                        style={{
                                            fontSize: 10,
                                            letterSpacing: 2.8,
                                            fontFamily: "'JetBrains Mono', monospace",
                                        }}
                                    >
                                        <textPath href="#heroCircle">
                                            GUT × SKIN — INSIDE OUT —
                                        </textPath>
                                    </text>
                                </svg>
                                <span className="h-2 w-2 rounded-full bg-sand" />
                            </div>
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: D + 1.2, duration: 0.8, ease: EASE }}
                            className="absolute -bottom-8 right-2 hidden max-w-[180px] text-right font-mono text-[10px] uppercase leading-relaxed tracking-[0.22em] text-earth md:block"
                        >
                            Where dermal science meets the gut
                        </motion.p>
                    </motion.div>
                </div>
            </div>

            {/* Scroll cue */}
            <motion.button
                data-testid="hero-scroll-indicator"
                onClick={() => scrollToId("philosophy")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: D + 1.35, duration: 0.8 }}
                className="absolute bottom-8 left-12 hidden items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-earth md:flex"
            >
                <span className="relative h-10 w-px overflow-hidden bg-ink/15">
                    <motion.span
                        className="absolute inset-x-0 top-0 h-4 bg-clay"
                        animate={{ y: [-16, 40] }}
                        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                    />
                </span>
                Scroll
            </motion.button>
        </section>
    );
}
